import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Grid } from '@mui/material';
import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { NumberInputControlled } from 'components/_basic/Form/NumberInput';
import { FormHeader } from 'components/_basic/FormHeader/FormHeader';
import { FormSectionCard } from 'components/_basic/FormSectionCard/FormSectionCard';
import { FORM_SECTION_CARD_VARIANT } from 'components/_basic/FormSectionCard/types';

import { concatWithDot } from 'helpers/string';

import { FundingDirectionsContainer } from './components/FundingDirectionsContainer';
import { GLOBAL_FIELD_NAMES, globalPrefix } from '../../Global/form';

export const FinancialNeedsExecSummary: React.FC = () => {
  const { t } = useTranslation();
  const { control } = useFormContext();

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.EXECUTIVE_SUMMARY,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_EXECUTIVE_SUMMARY.FINANCING_NEEDS,
  );

  return (
    <>
      <FormSectionCard variant={FORM_SECTION_CARD_VARIANT.LARGE}>
        <FormHeader
          locked={!canAccess}
          title={t('executiveSummary.title')}
          badgeText={t('executiveSummary.financialNeeds.title')}
        />
      </FormSectionCard>
      <FormSectionCard>
        <Grid container spacing={3}>
          <Grid item sm={6}>
            <NumberInputControlled
              control={control}
              id="executiveSummary.financialNeeds.totalCostOfYourProject"
              name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.TOTAL_COST)}
              label={t('executiveSummary.financialNeeds.totalCostOfYourProject')}
              adornmentType="currency"
              disabled={!canAccess}
              data-mirror-field={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.TOTAL_COST) + '.1_7_1'}
              defaultValue=""
            />
          </Grid>
          <Grid item sm={6}>
            <NumberInputControlled
              control={control}
              id="executiveSummary.financialNeeds.fundingAmount"
              name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.FUNDING_AMOUNT)}
              label={t('executiveSummary.financialNeeds.fundingAmount')}
              adornmentType="currency"
              disabled={!canAccess}
              defaultValue=""
            />
          </Grid>
          <Grid item sm={6}>
            <NumberInputControlled
              control={control}
              id="executiveSummary.financialNeeds.percentageOfFundingToCover"
              name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.PERCENTAGE_OF_FUNDING)}
              label={t('executiveSummary.financialNeeds.percentageOfFundingToCover')}
              adornmentType="percentage"
              disabled={!canAccess}
              defaultValue=""
            />
          </Grid>
          <Grid item sm={6}>
            <NumberInputControlled
              control={control}
              id="executiveSummary.financialNeeds.investmentAmount"
              name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.INVESTMENT_AMOUNT)}
              label={t('executiveSummary.financialNeeds.investmentAmount')}
              adornmentType="currency"
              disabled={!canAccess}
              defaultValue=""
            />
          </Grid>
          <Grid item sm={6}>
            <NumberInputControlled
              control={control}
              id="executiveSummary.financialNeeds.borrowAmount"
              name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.LOAN_BORROW_AMOUNT)}
              label={t('executiveSummary.financialNeeds.borrowAmount')}
              adornmentType="currency"
              disabled={!canAccess}
              defaultValue=""
            />
          </Grid>
        </Grid>
      </FormSectionCard>
      <FormSectionCard title={t('executiveSummary.financialNeeds.fundingDirections')} disabled={!canAccess}>
        <FundingDirectionsContainer disabled={!canAccess} />
      </FormSectionCard>
    </>
  );
};
